import { useMemo } from "react";
import { MedicalHistory, MedicalHistoryControllerApi } from "@/services";
import { asPaginatedContent, getConfigObject } from "@/utils/utils";
import { usePastMedicationHistoryCreate } from "@/queries/history/past-medication/use-patient-medication-history-create";
import { usePastMedicationHistoryUpdate } from "@/queries/history/past-medication/use-patient-medical-history-update";
import { useArchivePatientMedicineHistory } from "@/queries/history/past-medication/use-patient-medication-history-archive";
import { HistoryRow } from "../readmission-f2f-types";
import { mapMedicalHistoryToRow } from "../readmission-f2f-utils";
import {
  MEDICAL_HISTORY_RECORD_NAME,
  HISTORY_LIST_PAGE_SIZE,
  PATIENT_MEDICAL_HISTORY_LIST_KEY,
} from "../readmission-f2f-constant";
import {
  HistorySectionAdapter,
  UseHistorySection,
  useHistorySection,
} from "./use-history-section";

const buildPayload = (
  row: HistoryRow,
  patientId?: string,
  appointmentId?: string
): MedicalHistory => ({
  uuid: row.uuid,
  conditionName: row.name.trim(),
  note: row.notes.trim() || undefined,
  active: true,
  patient: { [patientId ?? ""]: "" },
  appointmentId: appointmentId ?? undefined,
});

export const useMedicalHistorySection = (): UseHistorySection => {
  const { mutateAsync: create, isPending: isCreating } =
    usePastMedicationHistoryCreate();
  const { mutateAsync: update, isPending: isUpdating } =
    usePastMedicationHistoryUpdate();
  const { mutateAsync: archive, isPending: isArchiving } =
    useArchivePatientMedicineHistory();

  const adapter: HistorySectionAdapter = useMemo(
    () => ({
      listKey: PATIENT_MEDICAL_HISTORY_LIST_KEY,
      recordName: MEDICAL_HISTORY_RECORD_NAME,
      isSaving: isCreating || isUpdating || isArchiving,
      fetchRows: ({ patientId, appointmentId, xTENANTID }) =>
        new MedicalHistoryControllerApi(getConfigObject())
          .getPatientMedicalHistory(
            patientId,
            appointmentId ?? undefined,
            0,
            HISTORY_LIST_PAGE_SIZE,
            undefined,
            undefined,
            true,
            false,
            xTENANTID
          )
          .then((res) =>
            (
              asPaginatedContent<MedicalHistory>(res?.data)?.data?.content ?? []
            ).map(mapMedicalHistoryToRow)
          ),
      createEntry: ({ row, patientId, appointmentId, xTENANTID }) =>
        create({
          medicalHistory: buildPayload(row, patientId, appointmentId),
          xTENANTID,
        }),
      updateEntry: ({ row, patientId, appointmentId, xTENANTID }) =>
        update({
          medicalHistory: buildPayload(row, patientId, appointmentId),
          xTENANTID,
        }),
      archiveEntry: ({ uuid, xTENANTID }) =>
        archive({ patientMedicalHistoryId: uuid, status: true, xTENANTID }),
    }),
    [create, update, archive, isCreating, isUpdating, isArchiving]
  );

  return useHistorySection(adapter);
};
